import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Image, Video, FileText, Plus, Trash2, Upload, Search, FolderOpen, Globe, Eye
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import AdminLayout from './AdminLayout';
import { errorText } from '../../lib/apiErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatFileSize(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function MediaLibraryTab() {
    const { getAuthHeader } = useAuth();
    const [items, setItems] = useState([]);
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedFolder, setSelectedFolder] = useState('all');
    const [selectedType, setSelectedType] = useState('all');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadMode, setUploadMode] = useState('file');
    const [uploadForm, setUploadForm] = useState({ name: '', description: '', file_type: 'document', file: null, folder: 'general', tags: '', external_url: '' });
    const [uploading, setUploading] = useState(false);

    useEffect(() => { loadData(); }, [search, selectedFolder, selectedType]);

    const loadData = async () => {
        try {
            const params = new URLSearchParams();
            if (search) params.append('search', search);
            if (selectedFolder !== 'all') params.append('folder', selectedFolder);
            if (selectedType !== 'all') params.append('file_type', selectedType);

            const [itemsRes, foldersRes] = await Promise.all([
                axios.get(`${API}/media-library?${params}`, { headers: getAuthHeader() }),
                axios.get(`${API}/media-library/folders`, { headers: getAuthHeader() }),
            ]);
            setItems(itemsRes.data);
            setFolders(foldersRes.data);
        } catch { toast.error('Failed to load media'); }
        finally { setLoading(false); }
    };

    const handleUpload = async () => {
        if (uploadMode === 'url') {
            if (!uploadForm.external_url.trim()) return toast.error('Please enter a URL');
            setUploading(true);
            try {
                await axios.post(`${API}/media-library`, {
                    name: uploadForm.name || uploadForm.external_url,
                    description: uploadForm.description,
                    file_type: uploadForm.file_type,
                    external_url: uploadForm.external_url,
                    folder: uploadForm.folder,
                    tags: uploadForm.tags.split(',').map(t => t.trim()).filter(t => t),
                }, { headers: getAuthHeader() });
                toast.success('Media added');
                setShowUploadModal(false);
                setUploadForm({ name: '', description: '', file_type: 'document', file: null, folder: 'general', tags: '', external_url: '' });
                setUploadMode('file');
                loadData();
            } catch { toast.error('Failed to add media'); }
            finally { setUploading(false); }
            return;
        }
        if (!uploadForm.file) return toast.error('Please select a file');
        setUploading(true);
        try {
            // Read first, then POST inside this try: the POST used to run in
            // reader.onload, outside it, so a 413/415 (CASA 5.2.1 sniffs the
            // bytes: images, PDF, mp4, office docs) was an unhandled rejection with no
            // toast, and `uploading` reset before the request even left.
            const dataUrl = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = () => reject(reader.error);
                reader.readAsDataURL(uploadForm.file);
            });
            const base64 = String(dataUrl).split(',')[1];
            await axios.post(`${API}/media-library`, {
                name: uploadForm.name || uploadForm.file.name,
                description: uploadForm.description,
                file_type: uploadForm.file_type,
                file_name: uploadForm.file.name,
                file_size: uploadForm.file.size,
                file_data: base64,
                folder: uploadForm.folder,
                tags: uploadForm.tags.split(',').map(t => t.trim()).filter(t => t),
            }, { headers: getAuthHeader() });
            toast.success('Media uploaded');
            setShowUploadModal(false);
            setUploadForm({ name: '', description: '', file_type: 'document', file: null, folder: 'general', tags: '', external_url: '' });
            setUploadMode('file');
            loadData();
        } catch (err) { toast.error(errorText(err, 'Upload failed')); }
        finally { setUploading(false); }
    };

    const handleDelete = async (mediaId) => {
        if (!window.confirm('Delete this media?')) return;
        try {
            await axios.delete(`${API}/media-library/${mediaId}`, { headers: getAuthHeader() });
            toast.success('Media deleted');
            loadData();
        } catch { toast.error('Delete failed'); }
    };

    const getFileIcon = (type) => {
        switch (type) {
            case 'image': return Image;
            case 'video': return Video;
            default: return FileText;
        }
    };

    if (loading) return <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-stone-700 border-t-transparent rounded-full animate-spin" /></div>;

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-lg font-semibold text-stone-900">Media Library</h2>
                    <p className="text-sm text-stone-500">Shared media assets accessible by all sales team members</p>
                </div>
                <Button onClick={() => setShowUploadModal(true)} className="bg-stone-900 hover:bg-stone-800">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Media
                </Button>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-4 mb-6">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input placeholder="Search media..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
                </div>
                <Select value={selectedFolder} onValueChange={setSelectedFolder}>
                    <SelectTrigger className="w-40">
                        <FolderOpen className="w-4 h-4 mr-2 text-stone-400" />
                        <SelectValue placeholder="All Folders" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Folders</SelectItem>
                        {folders.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={selectedType} onValueChange={setSelectedType}>
                    <SelectTrigger className="w-40">
                        <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="document">Documents</SelectItem>
                        <SelectItem value="image">Images</SelectItem>
                        <SelectItem value="video">Videos</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {items.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-stone-200 rounded-xl">
                    <Image className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                    <h3 className="text-lg font-semibold text-stone-700 mb-2">No media yet</h3>
                    <p className="text-stone-500 mb-4">Upload images, videos, and documents to share with your team</p>
                    <Button onClick={() => setShowUploadModal(true)}>Upload Your First Media</Button>
                </div>
            ) : (
                <div className="grid grid-cols-4 gap-4">
                    {items.map(item => {
                        const IconComponent = getFileIcon(item.file_type);
                        const isExternal = !!item.external_url;
                        return (
                            <div key={item.media_id} className="bg-white border border-stone-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow group" data-testid={`media-item-${item.media_id}`}>
                                <div className={`h-32 flex items-center justify-center relative ${
                                    item.file_type === 'image' ? 'bg-amber-50' :
                                    item.file_type === 'video' ? 'bg-violet-50' : 'bg-stone-100'
                                }`}>
                                    <IconComponent className={`w-12 h-12 ${
                                        item.file_type === 'image' ? 'text-amber-400' :
                                        item.file_type === 'video' ? 'text-violet-400' : 'text-stone-500'
                                    }`} />
                                    {isExternal && (
                                        <span className="absolute top-2 right-2 p-1 bg-white/80 rounded-md" title="External link">
                                            <Globe className="w-3.5 h-3.5 text-blue-500" />
                                        </span>
                                    )}
                                </div>
                                <div className="p-3">
                                    <p className="text-sm font-medium text-stone-800 truncate">{item.name}</p>
                                    <div className="flex items-center justify-between mt-1">
                                        <span className="text-xs text-stone-400">
                                            {isExternal ? 'External link' : formatFileSize(item.file_size)}
                                        </span>
                                        <span className="text-xs px-2 py-0.5 bg-stone-100 text-stone-500 rounded-full">{item.folder}</span>
                                    </div>
                                    <div className="flex items-center justify-end gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        {isExternal && (
                                            <a href={item.external_url} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-blue-50 rounded text-stone-400 hover:text-blue-500">
                                                <Eye className="w-4 h-4" />
                                            </a>
                                        )}
                                        <button onClick={() => handleDelete(item.media_id)} className="p-1.5 hover:bg-red-50 rounded text-stone-400 hover:text-red-500">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Upload Modal */}
            <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Add Media</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {/* Upload Mode Toggle */}
                        <div className="flex gap-2 p-1 bg-stone-100 rounded-lg">
                            <button
                                onClick={() => setUploadMode('file')}
                                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${uploadMode === 'file' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500'}`}
                                data-testid="upload-mode-file"
                            >
                                <Upload className="w-4 h-4 inline mr-1.5" />Upload File
                            </button>
                            <button
                                onClick={() => setUploadMode('url')}
                                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${uploadMode === 'url' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500'}`}
                                data-testid="upload-mode-url"
                            >
                                <Globe className="w-4 h-4 inline mr-1.5" />External URL
                            </button>
                        </div>

                        {uploadMode === 'file' ? (
                            <div>
                                <Label>File</Label>
                                <Input type="file" onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        setUploadForm(prev => ({
                                            ...prev,
                                            file,
                                            name: prev.name || file.name,
                                            file_type: file.type.startsWith('image/') ? 'image' :
                                                       file.type.startsWith('video/') ? 'video' : 'document'
                                        }));
                                    }
                                }} className="mt-1" data-testid="media-file-input" />
                            </div>
                        ) : (
                            <div>
                                <Label>External URL *</Label>
                                <Input
                                    value={uploadForm.external_url}
                                    onChange={(e) => setUploadForm(prev => ({ ...prev, external_url: e.target.value }))}
                                    placeholder="https://example.com/document.pdf"
                                    className="mt-1"
                                    data-testid="media-url-input"
                                />
                            </div>
                        )}

                        <div>
                            <Label>Name</Label>
                            <Input value={uploadForm.name} onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))} className="mt-1" placeholder={uploadMode === 'url' ? 'Display name for this media' : ''} data-testid="media-name-input" />
                        </div>
                        <div>
                            <Label>Description (optional)</Label>
                            <Textarea value={uploadForm.description} onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))} className="mt-1" rows={2} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>File Type</Label>
                                <Select value={uploadForm.file_type} onValueChange={(v) => setUploadForm(prev => ({ ...prev, file_type: v }))}>
                                    <SelectTrigger className="mt-1">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="document">Document</SelectItem>
                                        <SelectItem value="image">Image</SelectItem>
                                        <SelectItem value="video">Video</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Folder</Label>
                                <Input value={uploadForm.folder} onChange={(e) => setUploadForm(prev => ({ ...prev, folder: e.target.value }))} placeholder="general" className="mt-1" />
                            </div>
                        </div>
                        <div>
                            <Label>Tags (comma-separated)</Label>
                            <Input value={uploadForm.tags} onChange={(e) => setUploadForm(prev => ({ ...prev, tags: e.target.value }))} placeholder="sales, proposal, product" className="mt-1" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowUploadModal(false)}>Cancel</Button>
                        <Button onClick={handleUpload} disabled={uploading || (uploadMode === 'file' && !uploadForm.file) || (uploadMode === 'url' && !uploadForm.external_url.trim())} className="bg-stone-900 hover:bg-stone-800" data-testid="confirm-upload-btn">
                            {uploading ? 'Saving...' : uploadMode === 'url' ? 'Add Media' : 'Upload'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default function AdminMediaLibraryPage() {
    return (
        <AdminLayout title="Media Library" description="Shared media assets for your team">
            <MediaLibraryTab />
        </AdminLayout>
    );
}
